import { usePeopleTableColumns } from 'components/People/Table';
import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import { omit } from 'lodash';
import { createContext, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import accountService from 'services/account';
import batchService from 'services/batch';
import { useTableSortableHeaderCellEffectHandlers } from 'shell/components';
import { downloadCSV, formatAppUrl, getUserPreferredFullName } from 'utils/helper';
import { v4 as uuid } from 'uuid';

const handleFilters = (items, filters, setItems) => {
    const res = [...items].filter((item) => {
        const searchCond = getUserPreferredFullName(item).toLowerCase().includes(filters.search.toLowerCase());
        const rolesCond = filters.roles.length ? filters.roles.includes(item.role) : true;
        return searchCond && rolesCond;
    });
    setItems(res);
};

const PageContext = createContext();

const PageProvider = (props) => {
    const { course, accountUsers, setAccountUsers, addAccountUser, removeAccountUser } = useApp();
    const { auth } = useAuth();
    const { setDialog, closeDialog } = useDialog();
    const { message } = useSnackbar();
    const { t, i18n } = useTranslation();
    const columns = usePeopleTableColumns();

    const [filters, setFilters] = useState({
        open: false,
        roles: [],
        search: '',
    });
    const [checked, setChecked] = useState([]);
    const [inviteDialog, setInviteDialog] = useState({ open: false });

    const {
        sortedFilteredData: sortedFilteredAccountUsers,
        sortState,
        handleSetSortState,
    } = useTableSortableHeaderCellEffectHandlers(accountUsers, columns, filters, setAccountUsers, handleFilters);

    const resetFilters = useCallback(() => {
        setFilters((current) => ({
            ...current,
            roles: [],
        }));
        handleSetSortState({ order: 'asc', orderBy: null });
    }, [handleSetSortState]);

    const getFilterCount = (filters, sortState) => {
        let count = 0;
        if (sortState.orderBy) count++;
        if (filters.roles.length) count++;
        return count;
    };

    const removeUser = useCallback(
        async (accountUser) => {
            removeAccountUser(accountUser);
            await accountService.deleteAccount(accountUser.owner_account_id);
        },
        [removeAccountUser]
    );

    const sendInvitations = useCallback(
        async (type, users) => {
            const addedUsers = await batchService.sendInvitations({
                data: {
                    is_lti: false,
                    root_url: formatAppUrl(i18n.language, process.env.REACT_APP_BASE_NAME),
                    course_id: course.id,
                    type,
                    users: users.map((user) => {
                        const { email, first_name = '', last_name = '' } = user;
                        let accountUser = accountUsers.find((accountUser) => accountUser.email === email);
                        if (!accountUser) {
                            accountUser = {
                                owner_account_id: uuid(),
                                user_id: uuid(),
                                active: true,
                                created_on: new Date().getTime(),
                                role: type,
                                email: email?.toLowerCase(),
                                first_name,
                                last_name,
                            };
                        }
                        return {
                            id: accountUser.owner_account_id,
                            user_id: accountUser.user_id,
                            email: accountUser.email,
                            first_name: accountUser.first_name,
                            last_name: accountUser.last_name,
                        };
                    }),
                },
            });
            addedUsers.forEach((user) => {
                const accountUser = accountUsers.find(({ user_id }) => user_id === user.id);
                if (!accountUser) {
                    addAccountUser({
                        ...omit(user, ['id', 'account_id']),
                        owner_account_id: user.account_id,
                        user_id: user.id,
                    });
                }
            });
        },
        [accountUsers, addAccountUser, course?.id, i18n.language]
    );

    const getMemberActionOptions = useCallback(
        (accountUser) => {
            const canBeInvited = !Boolean(accountUser.login_activity);
            const canBeRemoved = auth.user_id !== accountUser.user_id;
            return [
                { key: 'copy', value: t('people.copy-email', 'Copy Email') },
                {
                    key: 'invite',
                    value: t('people.invite-instructor.resend-invitation', 'Resend Invitation'),
                    disabled: !canBeInvited,
                },
                { key: 'delete', value: t('controls.remove', 'Remove'), disabled: !canBeRemoved },
            ];
        },
        [auth.user_id, t]
    );

    const handleSelectMemberAction = useCallback(
        async (key, accountUser) => {
            switch (key) {
                case 'copy':
                    await navigator.clipboard.writeText(accountUser.email);
                    message.success(t('message.email-copied-success', 'Email copied successfully.'));
                    break;
                case 'invite':
                    message.success(
                        t('message.reinvited-success', '{{user}} successfully reinvited.', {
                            user: `${accountUser.role.charAt(0).toUpperCase()}${accountUser.role.slice(1)}`,
                        })
                    );
                    sendInvitations(accountUser.role, [accountUser]);
                    break;
                case 'delete':
                    setDialog((current) => ({
                        ...current,
                        open: true,
                        message: t('dialog.remove-member.title', 'Remove Member?'),
                        description: t(
                            'dialog.remove-member.description',
                            'Are you sure you want to remove {{user}} from {{course}}?',
                            {
                                user: getUserPreferredFullName(accountUser) || 'this member',
                                course: course.name,
                            }
                        ),
                        variant: 'destructive',
                        actionButtonText: t('controls.remove', 'Remove'),
                        cancelButtonText: t('controls.cancel', 'Cancel'),
                        onAction: () => {
                            removeUser(accountUser);
                            message.success(
                                t('message.removed-success', '{{user}} successfully removed.', {
                                    user: `${getUserPreferredFullName(accountUser) || 'Member'}`,
                                })
                            );
                            closeDialog();
                        },
                        onCancel: closeDialog,
                        onClose: closeDialog,
                    }));
                    break;
                default:
                    break;
            }
        },
        [closeDialog, course?.name, message, removeUser, sendInvitations, setDialog, t]
    );

    const removeCheckedUsers = useCallback(() => {
        setDialog((current) => ({
            ...current,
            open: true,
            message: t('dialog.remove-multiple-members.title', 'Remove {{count}} Members?', {
                count: checked.length,
            }),
            description: t('dialog.remove-multiple-members.description', 'This cannot be undone.'),
            variant: 'destructive',
            actionButtonText: t('controls.remove', 'Remove'),
            cancelButtonText: t('controls.cancel', 'Cancel'),
            onAction: () => {
                const toRemove = checked.filter((id) => id !== auth.owner_account_id);
                if (toRemove.length) {
                    toRemove.forEach((id) => removeUser({ owner_account_id: id }));
                    message.success(
                        t('message.removed-multiple-success', '{{count}} member(s) successfully removed.', {
                            count: checked.length,
                        })
                    );
                }
                setChecked([]);
                closeDialog();
            },
            onCancel: closeDialog,
            onClose: closeDialog,
        }));
    }, [auth.owner_account_id, checked, closeDialog, message, removeUser, setDialog, t]);

    const resendInvitations = useCallback(() => {
        setDialog((current) => ({
            ...current,
            open: true,
            message: t('dialog.reinvite-members.title', 'Reinvite Members?'),
            description: t(
                'dialog.reinvite-members.description',
                'Members who have already accepted an invitation will not be notified.'
            ),
            variant: 'primary',
            actionButtonText: t('dialog.reinvite-members.resend-invite', 'Resend Invite'),
            cancelButtonText: t('controls.cancel', 'Cancel'),
            onAction: () => {
                const toInvite = checked
                    .map((id) => accountUsers.find(({ owner_account_id }) => owner_account_id === id))
                    .filter(({ login_activity }) => !Boolean(login_activity));
                const instructorsToInvite = toInvite.filter((accountUser) => accountUser.role === 'instructor');
                if (instructorsToInvite.length) {
                    sendInvitations('instructor', instructorsToInvite);
                }
                if (instructorsToInvite.length)
                    message.success(
                        t('message.reinvited-multiple-success', '{{count}} member(s) successfully reinvited.', {
                            count: checked.length,
                        })
                    );
                setChecked([]);
                setDialog((current) => ({ ...current, open: false, onAction: () => {} }));
            },
            onCancel: closeDialog,
            onClose: closeDialog,
        }));
    }, [accountUsers, checked, closeDialog, message, sendInvitations, setDialog, t]);

    const downloadCourseRoster = useCallback(() => {
        const data = (accountUsers || []).map((accountUser) => {
            return [
                accountUser.display_name || accountUser.first_name || '',
                accountUser.last_name || '',
                accountUser.email,
            ];
        });
        return downloadCSV(`${course.name}_roster_template.csv`.toLowerCase().replaceAll(' ', '_'), [
            [t('table.first-name', 'First Name'), t('table.last-name', 'Last Name'), t('table.email', 'Email')],
            ...data,
        ]);
    }, [course?.name, accountUsers, t]);

    const value = useMemo(
        () => ({
            accountUsers: sortedFilteredAccountUsers,
            sortState,
            handleSetSortState,
            filters,
            setFilters,
            resetFilters,
            getFilterCount,
            checked,
            setChecked,
            inviteDialog,
            setInviteDialog,
            removeUser,
            sendInvitations,
            getMemberActionOptions,
            handleSelectMemberAction,
            removeCheckedUsers,
            resendInvitations,
            downloadCourseRoster,
            columns,
        }),
        [
            checked,
            downloadCourseRoster,
            filters,
            getMemberActionOptions,
            handleSelectMemberAction,
            handleSetSortState,
            inviteDialog,
            removeCheckedUsers,
            removeUser,
            resendInvitations,
            resetFilters,
            sendInvitations,
            sortState,
            sortedFilteredAccountUsers,
            columns,
        ]
    );

    return <PageContext.Provider value={value} {...props} />;
};

export { PageContext };
export default PageProvider;
